import React from 'react';
import { useNavigate } from 'react-router';
import { BlogArticle } from 'types/interfaces/blog';
import './BlogPreview.css';

interface BlogPreviewProps {
  articles: BlogArticle[];
  isLoading: boolean;
}

const BlogPreview: React.FC<BlogPreviewProps> = ({ articles, isLoading }) => {
  const navigate = useNavigate();

  const handleArticleClick = (slug: string) => {
    navigate(`/articles/${slug}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Show skeleton loading
  if (isLoading) {
    return (
      <div className="blog-preview">
        <div className="blog-preview-header">
          <h3>TradeMagic News</h3>
          <p>Articles, updates, and strategy from the Magic community.</p>
        </div>
        <div className="blog-preview-grid">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="blog-card-skeleton">
              <div className="skeleton-image" />
              <div className="skeleton-content">
                <div className="skeleton-title" />
                <div className="skeleton-subtitle" />
                <div className="skeleton-date" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Don't render if no articles (graceful degradation)
  if (!articles || articles.length === 0) {
    return null;
  }

  return (
    <div className="blog-preview">
      <div className="blog-preview-header">
        <h3>TradeMagic News</h3>
        <p>Articles, updates, and strategy from the Magic community.</p>
      </div>
      <div className="blog-preview-grid">
        {articles.map((article) => (
          <article
            key={article.id}
            className="blog-card"
            onClick={() => handleArticleClick(article.slug)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleArticleClick(article.slug);
              }
            }}
          >
            <div className="blog-card-image">
              {article.heroImageUrl ? (
                <img
                  src={article.heroImageUrl}
                  alt={article.title}
                  loading="lazy"
                />
              ) : (
                <div className="blog-card-placeholder" />
              )}
            </div>
            <div className="blog-card-content">
              <h4 className="blog-card-title">{article.title}</h4>
              {article.subtitle && (
                <p className="blog-card-subtitle">{article.subtitle}</p>
              )}
              {article.publishedAt && (
                <span className="blog-card-date">{formatDate(article.publishedAt)}</span>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default BlogPreview;
